import React from 'react';
import styled from 'styled-components';
import {FormField} from '../utils/use-form-field';
import {
  BlacklistRims,
  RangeSimModelData,
  RangeSimRimId,
  RangeTireSeasons,
  RangeTrimModel,
  TireSelectionOptions as TireSelectionTranslations,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {useFeatureAppId} from '../utils/use-feature-app-id';
import {useComponent} from '../component-library';
import {generateTestId, TestIdElementType} from '../utils/test';
import {SelectOption} from '../types';
import {SelectConsolidated} from './select-consolidated';
import {useDevConfig} from '../utils/use-dev-config';

export enum TireTypes {
  summer = 'summer',
  winter = 'winter',
  allSeason = 'allSeason',
}

interface TireSelectionProps {
  selectedModel: RangeTrimModel;
  tireTypeField: FormField<TireTypes>;
  rimField: FormField<RangeSimRimId>;
  tireSelectionTranslations: TireSelectionTranslations;
  blacklistRims: BlacklistRims | undefined;
}

interface MergedTireTypeAndRimOption extends SelectOption {
  rims: SelectOption[];
}

const getRimsForSeason = (
  simulationConstants: RangeSimModelData,
  season: RangeTireSeasons,
  blacklistRims: BlacklistRims | undefined
) =>
  Object.entries(simulationConstants.rims)
    .filter(
      ([_, rim]) =>
        rim.season === (season as string as RangeTireSeasons) &&
        !(
          blacklistRims &&
          blacklistRims[rim.season] &&
          blacklistRims[rim.season]!.includes(rim['size-inch'])
        )
    )
    .map(([rimId, rim]) => ({
      value: rimId,
      label: rim['size-string'],
    }));

export const TireSelection = ({
  selectedModel,
  tireTypeField,
  rimField,
  tireSelectionTranslations,
  blacklistRims,
}: TireSelectionProps): JSX.Element => {
  const Select = useComponent('Select');
  const featureAppId = useFeatureAppId();
  const {tireCombinedDropdowns} = useDevConfig();

  const simulationConstants = selectedModel.simulationConstants;

  const tireTypeOptions = React.useMemo(
    () => [
      {
        value: TireTypes.summer,
        label: tireSelectionTranslations.summerTireLabel,
      },
      {
        value: TireTypes.winter,
        label: tireSelectionTranslations.winterTireLabel,
      },
    ],
    [tireSelectionTranslations]
  );

  // Can be removed and added after line 121 once the all season has been made available for all the markets and versions
  Object.entries(selectedModel.simulationConstants.rims).forEach(([_, rim]) => {
    const allSeasonIndex = tireTypeOptions.findIndex(
      (tireTypeValue) => tireTypeValue.value === TireTypes.allSeason
    );
    if (rim.season === RangeTireSeasons.allSeason && allSeasonIndex === -1) {
      tireTypeOptions.push({
        value: TireTypes.allSeason,
        label: tireSelectionTranslations.allSeasonLabel || '',
      });
    }
  });

  const rimOptions = React.useMemo(
    () =>
      getRimsForSeason(
        simulationConstants,
        tireTypeField.value as string as RangeTireSeasons,
        blacklistRims
      ),
    [selectedModel, tireTypeField.value]
  );

  const mergedTireTypeAndRimOptions: MergedTireTypeAndRimOption[] =
    tireTypeOptions.map((tireTypeOption) => ({
      ...tireTypeOption,
      rims: getRimsForSeason(
        simulationConstants,
        tireTypeOption.value as unknown as RangeTireSeasons,
        blacklistRims
      ),
    }));

  const combinedTireAndRimOptions: SelectOption[] = [];
  mergedTireTypeAndRimOptions.forEach((mergedTireTypeOption) =>
    mergedTireTypeOption.rims.forEach((mergedRimOption) =>
      combinedTireAndRimOptions.push({
        value: `${mergedTireTypeOption.value},${mergedRimOption.value}`,
        label: `${mergedTireTypeOption.label} ${mergedRimOption.label}`,
      })
    )
  );

  return (
    <TireSelectsGrid combinedDropdowns={tireCombinedDropdowns}>
      {tireCombinedDropdowns ? (
        <SelectConsolidated
          formFieldA={tireTypeField}
          formFieldB={rimField}
          combinedSelectOptions={combinedTireAndRimOptions}
          testId={generateTestId({
            featureAppId,
            elementName: 'tire-type-tire-rim-size-combined',
            elementType: TestIdElementType.dynamic,
          })}
        />
      ) : (
        <>
          <TireTypeDropdownWrapper>
            <Select
              label={tireTypeField.label}
              selectedValue={tireTypeField.value}
              onChange={tireTypeField.onChange as (id: string) => void}
              options={tireTypeOptions}
              testid={generateTestId({
                featureAppId,
                elementName: 'tire-type',
                elementType: TestIdElementType.dynamic,
              })}
            />
          </TireTypeDropdownWrapper>
          {rimField.value !== undefined && rimOptions && (
            <RimSizeDropdownWrapper>
              <Select
                label={rimField.label}
                selectedValue={rimField.value}
                onChange={rimField.onChange as (id: string) => void}
                options={rimOptions}
                testid={generateTestId({
                  featureAppId,
                  elementName: 'tire-rim-size',
                  elementType: TestIdElementType.dynamic,
                })}
              />
            </RimSizeDropdownWrapper>
          )}
        </>
      )}
    </TireSelectsGrid>
  );
};

const TireSelectsGrid = styled.div<{combinedDropdowns: boolean}>`
  ${({combinedDropdowns}) =>
    combinedDropdowns
      ? ''
      : `
        display: grid;
        grid-template:
          'question question question'
          ' tires . rims ' / 3fr 1fr 3fr;
        `}
`;

const TireTypeDropdownWrapper = styled.div`
  grid-area: tires;
`;

const RimSizeDropdownWrapper = styled.div`
  grid-area: rims;
`;
